import { z } from "zod";

// Field Types
export const FieldKindSchema = z.enum(["integer", "decimal", "date", "text", "boolean"]);

// Operators by field type
export const NumericOpSchema = z.enum(["eq", "neq", "gt", "gte", "lt", "lte"]);
export const TextOpSchema = z.enum(["eq", "neq", "like"]);
export const BoolOpSchema = z.enum(["eq", "neq"]);
export const OperatorSchema = z.union([NumericOpSchema, TextOpSchema, BoolOpSchema]);

// Base filter structure
const BaseFilterSchema = z.object({
  kind: FieldKindSchema,
  name: z.string(),
  operator: OperatorSchema.optional(),
  dirty: z.boolean().optional(),
  isNew: z.boolean().optional(),
  value: z.unknown().optional(),
});

// Specific filter types with proper operator constraints
export const IntegerFilterSchema = BaseFilterSchema.extend({
  kind: z.literal("integer"),
  operator: NumericOpSchema.optional(),
  value: z.number().optional(),
});

export const DecimalFilterSchema = BaseFilterSchema.extend({
  kind: z.literal("decimal"),
  operator: NumericOpSchema.optional(),
  value: z.number().optional(),
});

export const DateFilterSchema = BaseFilterSchema.extend({
  kind: z.literal("date"),
  operator: NumericOpSchema.optional(),
  value: z.union([z.string(), z.date()]).optional(),
});

export const TextFilterSchema = BaseFilterSchema.extend({
  kind: z.literal("text"),
  operator: TextOpSchema.optional(),
  value: z.string().optional(),
});

export const BooleanFilterSchema = BaseFilterSchema.extend({
  kind: z.literal("boolean"),
  operator: BoolOpSchema.optional(),
  value: z.boolean().optional(),
});

// Union of all filter types
export const FilterSchema = z.discriminatedUnion("kind", [
  IntegerFilterSchema,
  DecimalFilterSchema,
  DateFilterSchema,
  TextFilterSchema,
  BooleanFilterSchema,
]);

// Order and grouping schemas
export const OrderSchema = z.object({
  name: z.string(),
  direction: z.enum(["ascending", "descending"]),
});

export const HiddenSchema = z.object({
  name: z.string(),
  hidden: z.boolean(),
});

// Main query schema
export const QuerySchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  filters: z.array(FilterSchema).default([]),
  hidden: z.array(HiddenSchema).optional().default([]),
  orderby: OrderSchema.optional(),
  groupby: z.string().optional(),
  dirty: z.boolean().optional(),
});

// Queries organized by page/table
export const QueriesSchema = z.record(z.string(), z.array(QuerySchema));

// User settings schema
export const UserSettingsSchema = z.object({
  city: z.string().optional(),
  state: z.string().optional(),
  timezone: z.string().optional(), // e.g., "America/Denver"
  format: z.string().optional(), // Date format like "MM/dd/yyyy"
  time: z.string().optional(), // Time format like "12" or "24"
  email_request: z.string().optional(),
});

// Complete profile meta_data schema
export const ProfileMetaDataSchema = z.object({
  city: z.string().optional(),
  state: z.string().optional(),
  timezone: z.string().optional(),
  format: z.string().optional(),
  time: z.string().optional(),
  email_request: z.string().optional(),
  queries: QueriesSchema.optional().default({}),
});

// Type exports
export type FieldKind = z.infer<typeof FieldKindSchema>;
export type NumericOp = z.infer<typeof NumericOpSchema>;
export type TextOp = z.infer<typeof TextOpSchema>;
export type BoolOp = z.infer<typeof BoolOpSchema>;
export type Operator = z.infer<typeof OperatorSchema>;

export type Filter = z.infer<typeof FilterSchema>;
export type IntegerFilter = z.infer<typeof IntegerFilterSchema>;
export type DecimalFilter = z.infer<typeof DecimalFilterSchema>;
export type DateFilter = z.infer<typeof DateFilterSchema>;
export type TextFilter = z.infer<typeof TextFilterSchema>;
export type BooleanFilter = z.infer<typeof BooleanFilterSchema>;

export type Order = z.infer<typeof OrderSchema>;
export type Hidden = z.infer<typeof HiddenSchema>;
export type Query = z.infer<typeof QuerySchema>;
export type Queries = z.infer<typeof QueriesSchema>;

export type UserSettings = z.infer<typeof UserSettingsSchema>;
export type ProfileMetaData = z.infer<typeof ProfileMetaDataSchema>;

// Validation helpers
export const isValidFilter = (filter: unknown): filter is Filter => {
  return FilterSchema.safeParse(filter).success;
};

export const isValidQuery = (query: unknown): query is Query => {
  return QuerySchema.safeParse(query).success;
};

export const isValidProfileMetaData = (data: unknown): data is ProfileMetaData => {
  return ProfileMetaDataSchema.safeParse(data).success;
};

// Default values
export const createDefaultQuery = (name?: string): Query => ({
  id: Date.now().toString(),
  name: name || "Default",
  filters: [],
  hidden: [],
  orderby: undefined,
  groupby: undefined,
  dirty: false,
});

export const createDefaultProfileMetaData = (): ProfileMetaData => ({
  city: undefined,
  state: undefined,
  timezone: "America/Denver",
  format: "MM/dd/yyyy",
  time: "12",
  email_request: "",
  queries: {},
});
